import { translateFn } from '../../../../../../../../utils/translator.helper';
import { html, PropertyValues, TemplateResult } from 'lit';
import { query } from 'lit/decorators.js';
import { GridViewWebComponent } from '../../../../../../../../grid/gridView';
import { ColumnDetails, GridEvents } from '../../../../../../../../grid/types/common';
import { SelectOption } from '../../../../../../../../ui/gzSelect/types';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../configs/page-config';
import { GzPolicyUiSettingsGridDataProvider } from '../dataProvider/GzPolicyUiSettingsGridDataProvider';
import { PolicyGridEvents } from '../../../events/events';
import { InlineRowActionsWebComponent } from '../../../../../../../../grid/uiElements/inlineRowActions';
import { State } from '../../../../../../../../grid/uiElements/inlineRowActions/types';
import { GzToastNotificationWebComponent } from '../../../../../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../../../../../ui/gzToastNotification/type';
import { GridCellEditWebComponent } from '../../../../../../../../grid/uiElements/gridCellEdit';
import { toggleVisibility } from '../../../../../../../../utils/htmlElement.utils';
import { GzPolicyUiSettingsBaseGrid } from '../../base';
import { ImageState } from '../../../../../../../../ui/gzImageRepo/types';
import { GzImageRepoWebComponent } from '../../../../../../../../ui/gzImageRepo';
import { GzInputNumberWebComponent } from '../../../../../../../../ui/gzInput';
import _ from 'lodash';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { GridColumnRenderers, SelectionStoreMode, ViewType } from '../../../../../../../../grid/gridView/types';
import { Filter } from '../../../../../../../../grid/gridFilters/types';
import { DefaultPolicyColumnKeys } from '../configs/column-config';
import { GzDialog } from '../../../../../../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../../../../../../ui/gzDialog';
import { GridFilterUiConfiguration } from '../../../../../../../../types/filters/types';

const VIEW_GRID_ID = 'policy-ui-settings-grid-v2';

export abstract class GzPolicyUiSettingsGridWebComponent<
  Column extends number,
  ColumnKeys extends string,
  GridItem extends object,
  GridItemFormValue,
  DataProvider extends GzPolicyUiSettingsGridDataProvider<Column, ColumnKeys, GridItem>
> extends GzPolicyUiSettingsBaseGrid<Column, GridItem, GridItemFormValue> {
  // column details to be set by the child class
  protected abstract allColumnDetails: ColumnDetails<Column, ColumnKeys>[];
  protected columnRenderers: GridColumnRenderers;

  // actions settings
  protected showHeaderDefaultActions = false;
  protected showHeaderCustomActionsInsideGrid = false;

  // filters settings
  protected hasFilters = false;
  protected filters: Filter<ColumnKeys>[] = [];
  protected predefinedFiltersKeys: ColumnKeys[] = [];
  protected filtersUiConfigs: Array<GridFilterUiConfiguration> = [];

  // page size settings
  protected pageSizeOptions: SelectOption[] = PAGE_SIZE_OPTIONS;
  protected pageSize: number = DEFAULT_PAGE_SIZE;

  // selection settings
  protected hasSelectableRows = false;

  // priority settings
  protected hasPriorityColumn = false;
  protected hasMoveToTopAction = false;
  protected hasBasicEditRowAction = false;

  // grid inline row actions
  protected hasCloneRowAction = false;
  protected hasEnableInlineRow = false;
  protected hasDisableInlineRow = false;
  protected customInlineRowActions: Array<SelectOption>;

  // translations
  protected translations: {
    editRow?: string;
    deleteRow?: string;
    moveToTop?: string;
    enableRow?: string;
    disableRow?: string;
    cloneRow?: string;
    emptyState?: string;
  } = {};

  // selectors
  @query('#policy-ui-settings-grid')
  protected gridComponent: GridViewWebComponent<Column, ColumnKeys>;

  protected emptyStateImage: ImageState = ImageState.NO_IMAGE;

  protected selectedItemsGridData = [];

  public getFormValue(): GridItem[] {
    return (this.gridComponent?.dataProvider as DataProvider)?.items || [];
  }

  public reset() {
    this.gridComponent.sortColumnName = undefined;
    this.gridComponent.sortDirection = undefined;

    super.reset();
    this.gridComponent.controller?.discardViewChanges();
  }

  protected computeGridValues() {
    if (!this.gridComponent || !this.gridComponent.dataProvider) {
      return;
    }
    this.gridComponent.propertiesSet = false;
    (this.gridComponent.dataProvider as DataProvider).items = this.initialGridItems;
    this.gridComponent.propertiesSet = true;

    this.onGridItemsChange();
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return null;
  }

  /**
   * Render header buttons. Override this method to add custom header buttons.
   */
  protected renderHeaderButtons(): TemplateResult | undefined {
    return;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.buildGrid();
  }

  protected renderGrid() {
    return html`<gz-grid-view
      class="grid-view"
      id="policy-ui-settings-grid"
      @onDeleteGridItem="${this.onDeleteGridItem.bind(this)}"
      @editRowEnabled="${this.onEditRowEnabled.bind(this)}"
      @editRowSave="${this.onEditRowSave.bind(this)}"
      @moveToTop="${this.onMoveToTop.bind(this)}"
      @onEnableGridItem="${this.onEnableGridItem.bind(this)}"
      @onDisableGridItem="${this.onDisableGridItem.bind(this)}"
      @onCloneRow="${this.onCloneRow.bind(this)}"
      @selectRows="${this.onSelectRows.bind(this)}"
      @activateInlineMenu="${this.onActivateInlineMenu.bind(this)}"
      @onBasicEditRow="${this.onBasicEditRow.bind(this)}"
    >
      <div class="header-buttons" slot="grid-header-actions-slot">${this.renderHeaderButtons()}</div>
      <div id="policies-grid-empty-state" slot="no-results--additional-data">${this.renderEmptyStateGrid()}</div>
    </gz-grid-view>`;
  }

  private buildGrid(): void {
    // view id
    this.gridComponent.viewId = VIEW_GRID_ID;

    // column renderers
    this.gridComponent.columns = this.columns;
    this.gridComponent.allColumnDetails = this.allColumnDetails;
    this.gridComponent.columnRenderers = this.columnRenderers;

    this.gridComponent.isCompactViewChecked = true;
    this.gridComponent.removeFilters = true;

    // refers to custom actions
    this.gridComponent.showGlobalActions = this.showHeaderCustomActionsInsideGrid;

    // refers to reset view
    this.gridComponent.hideHeaderActions = !this.showHeaderDefaultActions;
    this.gridComponent.defaultViewProps = {
      id: VIEW_GRID_ID,
      name: 'Default view',
      type: ViewType.DEFAULT,
      displayedColumns: this.columns,
      filters: this.filters,
      pageSize: DEFAULT_PAGE_SIZE,
      compactView: true,
    };

    // filters settings
    if (this.hasFilters) {
      this.gridComponent.filters = this.filters;
      this.gridComponent.removeFilters = false;
      this.gridComponent.predefinedFiltersKeys = this.predefinedFiltersKeys;
      this.gridComponent.filtersUiConfigs = this.filtersUiConfigs;
    }

    // pagination settings
    this.gridComponent.hasPagination = true;
    this.gridComponent.pageSizeOptions = this.pageSizeOptions;
    this.gridComponent.pageSize = this.pageSize;
    this.gridComponent.inlineRowActions = this.getInlineRowActions();

    // data provider settings
    this.gridComponent.dataProvider = this.getDataProvider();

    // selection settings
    this.gridComponent.hasSelectableRows = this.hasSelectableRows;
    if (this.hasSelectableRows) {
      this.gridComponent.selectionStoreConfig = {
        functionalMode: SelectionStoreMode.IDS,
        uniqueIdentifier: DefaultPolicyColumnKeys.POSITION,
      };
    }

    this.gridComponent.customStrings = {
      noSearchResultsString: this.translations.emptyState ?? '',
    };

    // set dynamic height
    this.gridComponent.dynamicHeight = true;

    // handler for grid items change
    this.onGridItemsChange();

    if (!this.gridComponent.started) {
      this.gridComponent.injectDependencies({
        translateFn: translateFn,
        getTimezone: () => {
          return Application.Main.getUser().profile.timezone;
        },
        getUserLanguage: () => {
          return Application.Main.getLanguage();
        },
      });
      this.gridComponent
        .start()
        .catch(console.error)
        .then(() => {
          const gzImageRepo = this.gridComponent.shadowRoot?.querySelector(
            '.no-results gz-image-repo'
          ) as GzImageRepoWebComponent;
          gzImageRepo.state = this.emptyStateImage;
        });
    }
  }

  private getInlineRowActions() {
    const inlineRowActions: Array<SelectOption> = [];
    // add edit action if enabled
    if (this.hasEditRowAction) {
      inlineRowActions.push({
        title: this.translations.editRow ?? '',
        value: GridEvents.EDIT_ROW,
      });
    }
    if (this.hasBasicEditRowAction) {
      inlineRowActions.push({
        title: this.translations.editRow ?? '',
        value: PolicyGridEvents.OnBasicEditRow,
      });
    }

    // add move to top action if enabled
    if (this.hasMoveToTopAction) {
      inlineRowActions.push({
        title: this.translations.moveToTop ?? '',
        value: PolicyGridEvents.MoveToTop,
      });
    }

    // add enable action if available
    if (this.hasEnableInlineRow) {
      inlineRowActions.push({
        title: this.translations.enableRow ?? '',
        value: PolicyGridEvents.OnEnableGridItem,
      });
    }

    // add disable action if available
    if (this.hasDisableInlineRow) {
      inlineRowActions.push({
        title: this.translations.disableRow ?? '',
        value: PolicyGridEvents.OnDisableGridItem,
      });
    }

    if (this.hasCloneRowAction) {
      inlineRowActions.push({
        title: this.translations.cloneRow ?? '',
        value: PolicyGridEvents.OnCloneRow,
      });
    }

    // add delete action if enabled
    if (this.hasDeleteRowAction) {
      inlineRowActions.push({
        title: this.translations.deleteRow ?? '',
        value: PolicyGridEvents.OnDeleteGridItem,
        customClass: 'danger',
      });
    }

    // add custom actions if available
    if (this.customInlineRowActions) {
      inlineRowActions.push(...this.customInlineRowActions);
    }

    return inlineRowActions;
  }

  protected onActivateInlineMenu(event: CustomEvent) {
    if (!this.hasMoveToTopAction) {
      return;
    }

    const moveToTopAction = this.gridComponent.inlineRowActions?.find(
      action => action.value === PolicyGridEvents.MoveToTop
    );
    if (moveToTopAction) {
      // disable move to top if item is already at the top having priority 1
      moveToTopAction.disabled = event.detail.itemData.item.defaultPositionInGrid === 1;
    }
  }

  protected onDeleteGridItem(event: CustomEvent) {
    const position = _.get(event.detail.rowData.item, DefaultPolicyColumnKeys.POSITION);
    this.removeGridItems([position]);
  }

  protected onDeleteSelectedItemsWithDialog(title: string, message: string) {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `<p>${message}</p>`;

    const footer = document.createElement('div');
    footer.innerHTML = `
      <gz-button id="buttonOne" type="outline" color="negative">${translateFn('Delete')}</gz-button>
      <gz-button id="buttonTwo" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
  `;

    const footerBtnOne = footer.querySelector('#buttonOne');
    const footerBtnTwo = footer.querySelector('#buttonTwo');

    const dialogConfigs: GzDialog = {
      headline: title,
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    if (footerBtnOne) {
      footerBtnOne.addEventListener('click', () => {
        this.onDeleteSelectedItems();
        dialog.close();
      });
    }

    if (footerBtnTwo) {
      footerBtnTwo.addEventListener('click', () => {
        dialog.dismiss();
      });
    }
  }

  protected onDeleteSelectedItems() {
    if (!this.hasSelectableRows) {
      return;
    }

    this.removeGridItems(this.selectedItemsGridData);
  }

  private removeGridItems(positions: number[]) {
    (this.gridComponent.dataProvider as DataProvider).removeItems(positions);
    this.gridComponent.controller?.handleGridItemsLoad(true, true);

    this.onGridItemsChange();
  }

  protected addGridItem(item: GridItem, position?: number) {
    (this.gridComponent.dataProvider as DataProvider).addItem(item, position);
    this.gridComponent.controller?.handleGridItemsLoad(true, true);
  }

  protected onEditRowEnabled(event: CustomEvent) {
    if (!this.hasPriorityColumn) {
      return;
    }

    const currentRowIndex = event.detail.rowData.index;
    const minPriority = 1;
    const maxPriority = (this.gridComponent.dataProvider as DataProvider).items.length;

    // set min and max priority for edited row
    const editedCells = this.gridComponent.controller?.view.getAllEditedCellsInRow(currentRowIndex + 2);
    editedCells?.forEach((gridCellEdit: GridCellEditWebComponent) => {
      const cellColumnKey = gridCellEdit.getAttribute('id');

      if (cellColumnKey === DefaultPolicyColumnKeys.POSITION) {
        (gridCellEdit.formField.Input as GzInputNumberWebComponent).min = minPriority;
        (gridCellEdit.formField.Input as GzInputNumberWebComponent).max = maxPriority;
      }
    });
  }

  protected onEditRowSave(event: CustomEvent) {
    if (!this.gridComponent.controller) {
      return;
    }

    const currentRowIndex = event.detail.rowData.index;
    // new item data
    const newItem: GridItem = event.detail.newData.item;
    const newPosition = _.get(newItem, DefaultPolicyColumnKeys.POSITION, 0);
    // old item data
    const oldItem: GridItem = event.detail.rowData.item;
    const oldPosition = _.get(oldItem, DefaultPolicyColumnKeys.POSITION, 0);

    // get inline menu
    const inlineMenu = this.gridComponent.controller.view.getRowInlineMenu(
      currentRowIndex + 2
    ) as InlineRowActionsWebComponent;

    // sanitize item
    const sanitizedItem = this.sanitizeGridItemBeforeEdit(newItem);

    // if row is not changed, set success and return
    const isEditedRowChanged = this.isEditedRowChanged(oldItem, sanitizedItem);
    const isPositionChanged = oldPosition !== newPosition;
    if (!isEditedRowChanged && !isPositionChanged) {
      inlineMenu.state = State.SUCCESS;
      return;
    }

    // set loading state
    inlineMenu.state = State.LOADING;
    inlineMenu.disabled = true;

    // validate grid item before edit
    let validationResponse: ValidationResult = { valid: true };
    if (isEditedRowChanged) {
      validationResponse = this.validateGridItemBeforeEdit(oldItem, sanitizedItem);
    }

    if (!validationResponse.valid) {
      // show error toast
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: validationResponse.error || '',
        severity: ToastSeverity.ERROR,
      });

      // revert changes
      const editedCells = this.gridComponent.controller.view.getAllEditedCellsInRow(currentRowIndex + 2);
      editedCells.forEach((gridCellEdit: GridCellEditWebComponent) => {
        this.revertGridCellEditValue(gridCellEdit, oldItem);
        this.revertGridCellEditedPriority(gridCellEdit, oldItem);
      });
      this.gridComponent.controller.view.dom.$grid.items = this.gridComponent.controller.view.dom.$grid.items.map(
        (item, index) => (index === currentRowIndex ? oldItem : item)
      );
    } else {
      // update grid item
      (this.gridComponent.dataProvider as DataProvider).updateItem(oldPosition, sanitizedItem);
      (this.gridComponent.dataProvider as DataProvider).moveItem(oldPosition, newPosition);
      this.onGridItemsChange();
    }

    // set inline menu state for 5 seconds
    inlineMenu.disabled = false;
    inlineMenu.state = validationResponse.valid ? State.SUCCESS : State.ERROR;

    // reload grid items if position changed and validation passed
    if (isPositionChanged && validationResponse.valid) {
      this.gridComponent.controller?.handleGridItemsLoad(true, true);
    }

    setTimeout(() => {
      inlineMenu.state = null;
    }, 5000);
  }

  protected onEditGridItemFromPopup(newItem: GridItem, oldItem: GridItem): ValidationResult {
    const newPosition = _.get(newItem, DefaultPolicyColumnKeys.POSITION);
    const oldPosition = _.get(oldItem, DefaultPolicyColumnKeys.POSITION, 1);

    // sanitize item
    const sanitizedItem = this.sanitizeGridItemBeforeEdit(newItem);

    // if row is not changed, set success and return
    const isEditedRowChanged = this.isEditedRowChanged(oldItem, sanitizedItem);
    const isPositionChanged = oldPosition !== newPosition;
    if (!isEditedRowChanged && !isPositionChanged) {
      return { valid: true };
    }

    // validate grid item before edit
    let validationResponse: ValidationResult = { valid: true };
    if (isEditedRowChanged) {
      validationResponse = this.validateGridItemBeforeEdit(oldItem, sanitizedItem);
    }

    if (!validationResponse.valid) {
      return validationResponse;
    }

    // update grid item
    (this.gridComponent.dataProvider as DataProvider).updateItem(oldPosition, sanitizedItem);

    // keep the same position if it is not defined
    if (newPosition !== undefined) {
      (this.gridComponent.dataProvider as DataProvider).moveItem(oldPosition, newPosition);
    }
    this.onGridItemsChange();
    this.gridComponent.controller?.handleGridItemsLoad(true, true);

    return { valid: true };
  }

  private revertGridCellEditedPriority(gridCellEdit: GridCellEditWebComponent, oldItem: GridItem) {
    if (!this.hasPriorityColumn || gridCellEdit.id !== DefaultPolicyColumnKeys.POSITION) {
      return;
    }

    gridCellEdit.value = _.get(oldItem, DefaultPolicyColumnKeys.POSITION);
  }

  private onMoveToTop(event: CustomEvent) {
    if (!this.hasPriorityColumn || !this.gridComponent.controller) {
      return;
    }

    // skip if already at top
    const oldPriority = _.get(event.detail.rowData.item, DefaultPolicyColumnKeys.POSITION, 0);
    if (oldPriority <= 1) {
      return;
    }

    // move to top
    (this.gridComponent.dataProvider as DataProvider).moveItem(oldPriority, 1);
    this.onGridItemsChange();

    this.gridComponent.controller?.handleGridItemsLoad(true, true);
  }

  protected onBasicEditRow(event: CustomEvent) {
    throw new Error('Not Implemented');
  }

  protected onCloneRow(event: CustomEvent) {
    throw new Error('Not Implemented');
  }

  /**
   * Get suggested name for cloned item. If the name already exists, add a number to the name.
   * @param name
   * @param field
   */
  protected getSuggestedNameForClonedItem(name: string, field: string): string {
    let testName,
      count = 0;

    do {
      testName = count
        ? translateFn('Clone of {name} ({count})', { name: name, count: count })
        : translateFn('Clone of {name}', { name: name });
      count = count + 1;
    } while ((this.gridComponent.dataProvider as DataProvider).items.some(item => item[field] === testName));

    return testName;
  }

  protected onEnableGridItem(event: CustomEvent) {
    throw new Error('Method not implemented.');
  }

  protected onDisableGridItem(event: CustomEvent) {
    throw new Error('Method not implemented.');
  }

  protected onSelectRows(event: CustomEvent) {
    this.selectedItemsGridData = event.detail.selectionStoreData;
  }

  protected onGridItemsChange(): void {}

  /**
   * Get data provider with grid items.
   */
  protected abstract getDataProvider(): DataProvider;
}
